import { v4 as uuidv4 } from "uuid";
import useApi from "../../hooks/useApi";
import { buildUrlWithParams, CHATBOT_MESSAGES_ENDPOINT, CHATBOT_MESSAGE_FEEDBACK_ENDPOINT } from "../../services/services";
import { ChatbotAnswer, ChatbotAnswerSchema, ChatbotFeedback, ChatbotFeedbackRequest, ChatbotQuestionRequest } from "../../services/chatbot";
import { useChatbotStore } from "../stores/useChatbotStore";

const ERROR_MESSAGE = "Désolé, une erreur est survenue. Merci de réessayer dans un instant.";

/**
 * Le store useChatbotStore ne fait que porter l'état (mémoire de session).
 * Les appels réseau vivent ici, dans un hook — comme pour les autres écrans du
 * projet (cf. useAdhesionManagement, useMesInscriptions) — car useApi() est lui-même
 * un hook (état de chargement interne, dépendance à useAuth) et ne peut donc pas
 * être invoqué depuis une action Zustand.
 */
export const useChatbot = () => {
    const { execute } = useApi();
    const { messages, conversationId, loading, addMessage, updateMessage, setLoading, setConversationId, reset } = useChatbotStore();

    const sendQuestion = async (question: string) => {
        const trimmed = question.trim();
        if (!trimmed || loading) {
            return;
        }

        addMessage({ id: uuidv4(), role: "user", content: trimmed });
        setLoading(true);

        const data: ChatbotQuestionRequest = {
            question: trimmed,
            ...(conversationId != null ? { conversationId } : {}),
        };

        const result = await execute<ChatbotAnswer>(
            { method: "POST", url: CHATBOT_MESSAGES_ENDPOINT, data },
            ChatbotAnswerSchema
        );

        if (result.successData) {
            setConversationId(result.successData.conversationId);
            addMessage({
                id: uuidv4(),
                role: "assistant",
                content: result.successData.answer,
                serverId: result.successData.messageId,
                sources: result.successData.sources,
            });
        } else {
            addMessage({ id: uuidv4(), role: "assistant", content: ERROR_MESSAGE, error: true });
        }

        setLoading(false);
    };

    const setFeedback = async (messageId: string, feedback: ChatbotFeedback) => {
        const message = messages.find((m) => m.id === messageId);
        if (!message || !message.serverId || message.feedbackPending || message.feedback === feedback) {
            return;
        }

        updateMessage(messageId, { feedbackPending: true });

        const data: ChatbotFeedbackRequest = { feedback };
        const result = await execute<void>({
            method: "POST",
            url: buildUrlWithParams(CHATBOT_MESSAGE_FEEDBACK_ENDPOINT, { messageId: message.serverId }),
            data,
        });

        updateMessage(messageId, {
            feedbackPending: false,
            ...(result.success ? { feedback } : {}),
        });
    };

    return { messages, conversationId, loading, sendQuestion, setFeedback, reset };
};
